/**
 * العملات وأسعار الصرف.
 *
 * الدفاتر بالجنيه المصري دائمًا: كل بند قيد مدين أو دائن بالجنيه. الحساب
 * بعملة أجنبية (بنك بالدولار، خزينة باليورو) يحمل إلى جانب ذلك المبلغ بعملته
 * وسعر الصرف يوم الحركة، فيُعرف رصيده بعملته ويُعاد تقييمه بسعر اليوم في
 * القوائم دون أن يُمسّ الدفتر.
 */

/** عملة الدفاتر. */
export const BASE_CURRENCY = 'EGP';

/** العملات المتاحة للخزائن وحسابات الدليل — أضف هنا لتظهر في كل مكان. */
export const SUPPORTED_CURRENCIES = ['EGP', 'USD', 'EUR', 'SAR', 'AED', 'GBP'] as const;
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

export interface CurrencyDef {
  code: CurrencyCode;
  /** الاسم بالعربي للقوائم المنسدلة. */
  label: string;
  /** الرمز بجوار المبلغ. */
  symbol: string;
}

export const CURRENCY_DEFS: readonly CurrencyDef[] = [
  { code: 'EGP', label: 'جنيه مصري', symbol: 'ج.م' },
  { code: 'USD', label: 'دولار أمريكي', symbol: '$' },
  { code: 'EUR', label: 'يورو', symbol: '€' },
  { code: 'SAR', label: 'ريال سعودي', symbol: 'ر.س' },
  { code: 'AED', label: 'درهم إماراتي', symbol: 'د.إ' },
  { code: 'GBP', label: 'جنيه إسترليني', symbol: '£' },
];

export const CURRENCY_MAP = Object.fromEntries(CURRENCY_DEFS.map((c) => [c.code, c])) as Readonly<
  Record<CurrencyCode, CurrencyDef>
>;

/** العملات الأجنبية وحدها — ما له سعر صرف. */
export const FOREIGN_CURRENCIES = SUPPORTED_CURRENCIES.filter((c) => c !== BASE_CURRENCY);

export function isForeignCurrency(code: string | null | undefined): boolean {
  return !!code && code !== BASE_CURRENCY;
}

/** رمز العملة للعرض، أو الكود نفسه إن لم تكن معرّفة. */
export function currencySymbol(code: string | null | undefined): string {
  if (!code) return CURRENCY_MAP.EGP.symbol;
  return (CURRENCY_MAP as Record<string, CurrencyDef | undefined>)[code]?.symbol ?? code;
}

export const EXCHANGE_RATE_SOURCES = ['AUTO', 'MANUAL'] as const;
export type ExchangeRateSource = (typeof EXCHANGE_RATE_SOURCES)[number];

/** سعر عملة واحدة كما يُعرض في شاشة الأسعار. */
export interface ExchangeRateView {
  currency: CurrencyCode;
  /** كم جنيهًا تساوي الوحدة. */
  rate: number | null;
  /** يوم السعر — قد يسبق اليوم إن تعذّر جلب سعر اليوم فبقي آخر سعر معروف. */
  date: string | null;
  source: ExchangeRateSource | null;
}

/** تقريب المبلغ بالجنيه إلى قرشين. */
export function toBase(fxAmount: number, rate: number): number {
  return Math.round(fxAmount * rate * 100) / 100;
}

/** سعر الصرف يُحفظ بست خانات عشرية. */
export function roundRate(rate: number): number {
  return Math.round(rate * 1_000_000) / 1_000_000;
}

/**
 * بيانات العملة الأجنبية على بند أو سطر قائمة.
 *
 * `amount` بإشارة الطبيعة نفسها التي يحملها المبلغ بالجنيه بجواره.
 */
export interface FxFigure {
  currency: string;
  amount: number;
  rate: number;
}
